import { OwnerOnboardingStatus, OwnerStatusWidgetModel } from '@app/core/models';

const STATUS_COPY: Record<
  OwnerOnboardingStatus,
  Pick<OwnerStatusWidgetModel, 'title' | 'message' | 'supportMessage' | 'nextOwnerAction' | 'nextAdminAction'>
> = {
  RegistrationPending: {
    title: 'Complete registration',
    message: 'Your client account will be activated after the property connection and payment complete successfully.',
    supportMessage: 'Return to registration to finish secure billing.',
    nextOwnerAction: 'Complete billing to activate your account.',
    nextAdminAction: 'No admin action is required while payment is pending.'
  },
  PendingOwnerConfirmation: {
    title: 'Confirm your email to continue',
    message: 'Your account has been created. Please confirm your email. Our team will continue setup after that.',
    supportMessage: 'Need help confirming your email? Contact support and we will help verify the account.',
    nextOwnerAction: 'Confirm your email address.',
    nextAdminAction: 'Wait for owner email confirmation.'
  },
  PendingAdminSetup: {
    title: 'Your account is waiting for setup',
    message: 'Your account is created. Our team will begin onboarding shortly.',
    supportMessage: 'Our team has the account in the setup queue.',
    nextOwnerAction: 'No action needed right now.',
    nextAdminAction: 'Start client setup.'
  },
  AdminInProgress: {
    title: 'Onboarding is in progress',
    message: 'Your onboarding is in progress. We will notify you when setup is completed.',
    supportMessage: 'Our team is completing your profile, properties, PMS mapping, and initial import.',
    nextOwnerAction: 'No action needed right now.',
    nextAdminAction: 'Continue admin setup.'
  },
  AwaitingAdminReview: {
    title: 'Setup is almost complete',
    message: 'Your setup is almost complete and is awaiting final review.',
    supportMessage: 'Final activation checks are underway.',
    nextOwnerAction: 'No action needed right now.',
    nextAdminAction: 'Review onboarding checklist.'
  },
  SetupFailed: {
    title: 'Setup needs attention',
    message: 'We hit an issue during onboarding. Our team is reviewing it.',
    supportMessage: 'Please contact support if you need an update while we review the issue.',
    nextOwnerAction: 'Contact support if this is urgent.',
    nextAdminAction: 'Resolve setup issue or reopen onboarding.'
  },
  Suspended: {
    title: 'Account suspended',
    message: 'Your account is currently suspended. Please contact support.',
    supportMessage: 'Access is paused until support resolves the account status.',
    nextOwnerAction: 'Contact support.',
    nextAdminAction: 'Review suspension before reopening.'
  },
  Live: {
    title: '',
    message: '',
    supportMessage: null,
    nextOwnerAction: null,
    nextAdminAction: null
  },
  Completed: {
    title: '',
    message: '',
    supportMessage: null,
    nextOwnerAction: null,
    nextAdminAction: null
  }
};

export const buildOwnerStatusWidget = (
  status: OwnerOnboardingStatus,
  overrides?: Partial<OwnerStatusWidgetModel> | null
): OwnerStatusWidgetModel => {
  const copy = STATUS_COPY[status] ?? STATUS_COPY.PendingAdminSetup;

  return {
    status,
    title: overrides?.title || copy.title,
    message: overrides?.message || copy.message,
    isBlocking: status !== 'Live' && status !== 'Completed',
    canDismiss: status === 'Live' || status === 'Completed',
    lastUpdatedAt: overrides?.lastUpdatedAt ?? null,
    supportMessage: overrides?.supportMessage ?? copy.supportMessage ?? null,
    nextOwnerAction: overrides?.nextOwnerAction ?? copy.nextOwnerAction ?? null,
    nextAdminAction: overrides?.nextAdminAction ?? copy.nextAdminAction ?? null
  };
};
